import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Sparkles, Loader2, Clock, MessageSquare, ChevronDown, Trash2, Database, BarChart3, Bot, Link2 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { matchRateApi } from '@/api/client'
import { useAuthStore } from '@/store/authStore'
import { showToast } from '@/store/uiStore'
import { StructuredAnswer } from './StructuredAnswer'

interface QuestionRecord {
  id: number
  question: string
  answer: string | null
  context_sent: string | null
  country_codes_extracted: string | null
  answer_type: string | null
  structured_data: string | null
  status: string
  error_message: string | null
  created_by_name: string | null
  created_at: string
}

function unwrap(res: any): any {
  return res?.data?.data || res?.data || null
}

function unwrapList(res: any): any[] {
  const d = res?.data?.data || res?.data || []
  return Array.isArray(d) ? d : []
}

function formatDate(d: string) {
  // D1 stores UTC without a Z suffix — append it so JS parses as UTC, then toLocaleString converts to local tz
  const utc = d.endsWith('Z') ? d : d + 'Z'
  return new Date(utc).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

// Styled markdown components for AI answers
const mdComponents = {
  h1: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h1 className="text-base font-bold text-[var(--text-primary)] mt-5 mb-2 pb-1 border-b border-[var(--border-color)]" {...props}>{children}</h1>
  ),
  h2: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h2 className="text-sm font-bold text-[var(--text-primary)] mt-4 mb-2" {...props}>{children}</h2>
  ),
  h3: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h3 className="text-sm font-semibold text-[var(--text-primary)] mt-3 mb-1.5" {...props}>{children}</h3>
  ),
  h4: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h4 className="text-xs font-semibold text-[var(--text-primary)] mt-3 mb-1 uppercase tracking-wide" {...props}>{children}</h4>
  ),
  p: ({ children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p className="text-xs text-[var(--text-secondary)] my-1.5 leading-relaxed" {...props}>{children}</p>
  ),
  ul: ({ children, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
    <ul className="text-xs text-[var(--text-secondary)] my-1.5 ml-4 space-y-0.5 list-disc" {...props}>{children}</ul>
  ),
  ol: ({ children, ...props }: React.OlHTMLAttributes<HTMLOListElement>) => (
    <ol className="text-xs text-[var(--text-secondary)] my-1.5 ml-4 space-y-0.5 list-decimal" {...props}>{children}</ol>
  ),
  li: ({ children, ...props }: React.LiHTMLAttributes<HTMLLIElement>) => (
    <li className="text-xs text-[var(--text-secondary)] leading-relaxed" {...props}>{children}</li>
  ),
  table: ({ children, ...props }: React.HTMLAttributes<HTMLTableElement>) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-[var(--border-color)]">
      <table className="w-full text-xs border-collapse" {...props}>{children}</table>
    </div>
  ),
  thead: ({ children, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => (
    <thead className="bg-violet-500/10" {...props}>{children}</thead>
  ),
  th: ({ children, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) => (
    <th className="px-2.5 py-1.5 text-left text-[10px] font-semibold text-[var(--text-primary)] border-b border-[var(--border-color)] whitespace-nowrap" {...props}>{children}</th>
  ),
  td: ({ children, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) => (
    <td className="px-2.5 py-1.5 text-[11px] text-[var(--text-secondary)] border-b border-[var(--border-color)]/50" {...props}>{children}</td>
  ),
  tr: ({ children, ...props }: React.HTMLAttributes<HTMLTableRowElement>) => (
    <tr className="hover:bg-[var(--bg-tertiary)]/50 transition-colors" {...props}>{children}</tr>
  ),
  hr: (props: React.HTMLAttributes<HTMLHRElement>) => (
    <hr className="my-4 border-[var(--border-color)]" {...props} />
  ),
  strong: ({ children, ...props }: React.HTMLAttributes<HTMLElement>) => (
    <strong className="font-semibold text-[var(--text-primary)]" {...props}>{children}</strong>
  ),
  blockquote: ({ children, ...props }: React.BlockquoteHTMLAttributes<HTMLQuoteElement>) => (
    <blockquote className="my-2 pl-3 border-l-2 border-violet-500/50 italic text-[var(--text-muted)] text-xs" {...props}>{children}</blockquote>
  ),
  code: ({ children, ...props }: React.HTMLAttributes<HTMLElement>) => (
    <code className="px-1 py-0.5 rounded bg-[var(--bg-tertiary)] text-[10px] font-mono text-violet-400" {...props}>{children}</code>
  ),
}

const EXAMPLE_QUESTIONS = [
  'Can we do a 2+2 check in China? What data will the customer need?',
  'What is the best match rate we can get in Australia and New Zealand?',
  'What national ID is required for Datazoo verification in India?',
  'Which countries support Gov ID verification with Socure?',
]

// ─── Past Question Card ────────────────────────────────────────────────

function QuestionCard({ q, onDelete, defaultExpanded }: { q: QuestionRecord; onDelete: (id: number) => void; defaultExpanded?: boolean }) {
  const [expanded, setExpanded] = useState(defaultExpanded || false)
  const [showContext, setShowContext] = useState(false)

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/dashboard/presales/tools/match-rate?question=${q.id}`
    await navigator.clipboard.writeText(shareUrl)
    showToast('Link copied to clipboard', 'success')
  }

  return (
    <div className={`rounded-lg border bg-[var(--bg-secondary)] overflow-hidden ${defaultExpanded ? 'border-violet-500/30' : 'border-[var(--border-color)]'}`}>
      <div className="flex items-start">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 text-left px-4 py-3 hover:bg-[var(--bg-tertiary)] transition-colors"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[var(--text-primary)]">{q.question}</p>
              <div className="flex items-center gap-3 mt-1 text-[10px] text-[var(--text-muted)]">
                {q.country_codes_extracted && <span>{q.country_codes_extracted}</span>}
                {q.created_by_name && <span>by {q.created_by_name}</span>}
                <span className="flex items-center gap-1"><Clock size={10} />{formatDate(q.created_at)}</span>
              </div>
            </div>
            <ChevronDown size={14} className={`text-[var(--text-muted)] transition-transform flex-shrink-0 mt-1 ${expanded ? 'rotate-180' : ''}`} />
          </div>
        </button>
        <button
          onClick={handleShare}
          className="p-3 text-[var(--text-muted)] hover:text-violet-400 transition-colors flex-shrink-0"
          title="Share question"
        >
          <Link2 size={14} />
        </button>
        <button
          onClick={() => onDelete(q.id)}
          className="p-3 text-[var(--text-muted)] hover:text-red-400 transition-colors flex-shrink-0"
          title="Delete question"
        >
          <Trash2 size={14} />
        </button>
      </div>
      {expanded && q.answer && (() => {
        const struct = parseStructuredData(q.structured_data)
        const hasStruct = struct && (struct as { countries?: unknown[] }).countries?.length
        return (
          <div className="px-4 pb-4 border-t border-[var(--border-color)] pt-3">
            {hasStruct ? (
              <StructuredAnswer
                answer_type={q.answer_type || 'general'}
                structured_data={struct as { answer_type: string; countries: [] }}
                ai_answer={q.answer}
              />
            ) : (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{q.answer}</ReactMarkdown>
            )}
            {q.context_sent && (
              <div className="mt-3 border-t border-[var(--border-color)] pt-2">
                <button
                  onClick={() => setShowContext(!showContext)}
                  className="text-[10px] text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
                >
                  {showContext ? 'Hide' : 'View'} prompt sent to AI
                </button>
                {showContext && (
                  <pre className="mt-2 p-3 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[10px] text-[var(--text-muted)] overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap font-mono">
                    {q.context_sent}
                  </pre>
                )}
              </div>
            )}
          </div>
        )
      })()}
      {expanded && q.status === 'error' && (
        <div className="px-4 pb-4 border-t border-[var(--border-color)] pt-3">
          <p className="text-xs text-red-400">{q.error_message || 'Unknown error'}</p>
        </div>
      )}
    </div>
  )
}

function parseStructuredData(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null
  try { return JSON.parse(raw) } catch { return null }
}

function CurrentAnswerView({ answer }: { answer: QuestionRecord }) {
  const [showContext, setShowContext] = useState(false)
  const structured = parseStructuredData(answer.structured_data)
  const hasStructured = structured && (structured as { countries?: unknown[] }).countries?.length

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/dashboard/presales/tools/match-rate?question=${answer.id}`
    await navigator.clipboard.writeText(shareUrl)
    showToast('Link copied to clipboard', 'success')
  }

  return (
    <div className="rounded-lg border border-violet-500/30 bg-violet-500/5 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-violet-400" />
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Answer</h3>
        {answer.country_codes_extracted && (
          <span className="text-[10px] text-[var(--text-muted)]">Countries: {answer.country_codes_extracted}</span>
        )}
        <div className="flex-1" />
        <button
          onClick={handleShare}
          className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] rounded-lg bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] border border-[var(--border-color)] transition-colors"
        >
          <Link2 size={12} /> Share
        </button>
      </div>
      {hasStructured ? (
        <StructuredAnswer
          answer_type={answer.answer_type || 'general'}
          structured_data={structured as { answer_type: string; countries: [] }}
          ai_answer={answer.answer || ''}
        />
      ) : (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{answer.answer!}</ReactMarkdown>
      )}
      {answer.context_sent && (
        <div className="mt-3 border-t border-violet-500/20 pt-2">
          <button
            onClick={() => setShowContext(!showContext)}
            className="text-[10px] text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
          >
            {showContext ? 'Hide' : 'View'} prompt sent to AI
          </button>
          {showContext && (
            <pre className="mt-2 p-3 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[10px] text-[var(--text-muted)] overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap font-mono">
              {answer.context_sent}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Methodology Panel ────────────────────────────────────────────────

function MethodologyPanel() {
  const [open, setOpen] = useState(false)

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[var(--bg-tertiary)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Database size={14} className="text-[var(--text-muted)]" />
          <span className="text-xs font-medium text-[var(--text-secondary)]">Data Sources & Methodology</span>
        </div>
        <ChevronDown size={14} className={`text-[var(--text-muted)] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-[var(--border-color)] pt-3 space-y-4 text-xs text-[var(--text-secondary)]">
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Bot size={12} className="text-violet-400" />
              <span className="font-semibold text-[var(--text-primary)]">AI-Powered Answers (Cassidy AI)</span>
            </div>
            <p className="text-[var(--text-muted)] leading-relaxed">
              Questions are answered by Cassidy AI, FrankieOne's internal AI platform. The AI receives a system prompt defining its role as a KYC specialist,
              along with pre-computed factual data from our databases. You can view the exact prompt sent for each answer by clicking "View prompt sent to AI" on any response.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-[var(--text-primary)] mb-1.5">Data provided to the AI</h4>
            <p className="text-[var(--text-muted)] mb-2 leading-relaxed">
              Before sending a question, the system extracts country codes and vendors from your question, then pre-computes factual data from these sources:
            </p>
            <div className="space-y-1.5 font-mono text-[10px]">
              <div className="p-2 rounded bg-[var(--bg-primary)] border border-[var(--border-color)]">
                <span className="text-violet-400">v_kyc_country</span>
                <span className="text-[var(--text-muted)] ml-2">— Country-level coverage flags, recommended vendors, match rates per source</span>
              </div>
              <div className="p-2 rounded bg-[var(--bg-primary)] border border-[var(--border-color)]">
                <span className="text-violet-400">kyc_data_sources</span>
                <span className="text-[var(--text-muted)] ml-2">— Detailed source profiles: mandatory PII, consent, Gov ID, verifiable fields</span>
              </div>
              <div className="p-2 rounded bg-[var(--bg-primary)] border border-[var(--border-color)]">
                <span className="text-violet-400">vendor_address_details</span>
                <span className="text-[var(--text-muted)] ml-2">— Per-source address field verification with gap assessments</span>
              </div>
              <div className="p-2 rounded bg-[var(--bg-primary)] border border-[var(--border-color)]">
                <span className="text-violet-400">vendor_national_ids</span>
                <span className="text-[var(--text-muted)] ml-2">— Accepted national ID types with format patterns</span>
              </div>
              <div className="p-2 rounded bg-[var(--bg-primary)] border border-[var(--border-color)]">
                <span className="text-violet-400">team_insights</span>
                <span className="text-[var(--text-muted)] ml-2">— Critical warnings from the KYC product team</span>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <BarChart3 size={12} className="text-violet-400" />
              <span className="font-semibold text-[var(--text-primary)]">Production Pass Rates (ThoughtSpot)</span>
            </div>
            <p className="text-[var(--text-muted)] mb-2 leading-relaxed">
              Real production KYC pass rates are fetched from ThoughtSpot and provided to the AI as pre-computed facts.
              These are actual entity-level PASS/FAIL/REFER outcomes from live FrankieOne traffic.
            </p>
            <div className="space-y-1.5">
              <div className="p-2.5 rounded bg-[var(--bg-primary)] border border-[var(--border-color)]">
                <p className="font-mono text-[10px] text-violet-400 mb-1">Entity-level query:</p>
                <pre className="text-[10px] text-[var(--text-muted)] whitespace-pre-wrap leading-relaxed">{`[Verification Class] = "KYC" [Country] = "<ISO3>" [Country] [Trans Status] [Created] >= "<time_bracket>" count [Entity Id]`}</pre>
              </div>
              <div className="p-2.5 rounded bg-[var(--bg-primary)] border border-[var(--border-color)]">
                <p className="font-mono text-[10px] text-violet-400 mb-1">Source-level query:</p>
                <pre className="text-[10px] text-[var(--text-muted)] whitespace-pre-wrap leading-relaxed">{`[Verification Class] = "KYC" [Country] = "<ISO3>" [Country] [Provider Source] [Trans Status] [Created] >= "<time_bracket>" count [Entity Id]`}</pre>
              </div>
            </div>
            <p className="text-[var(--text-muted)] mt-2 leading-relaxed">
              <span className="font-medium text-[var(--text-secondary)]">Worksheet:</span>{' '}
              <span className="font-mono text-[10px]">f7d183a2-9693-412b-b78f-b4d0a4ac07b7</span>.
              Time brackets: 1 month, 3 months, 12 months.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-[var(--text-primary)] mb-1.5">AI system prompt summary</h4>
            <p className="text-[var(--text-muted)] leading-relaxed">
              The AI is instructed to act as a KYC specialist, read all data sections (country summaries, per-source details, address verification, national IDs, team insights, production pass rates),
              start with a TL;DR summary table, cite specific sources and vendors, flag consent requirements,
              and be honest about limitations. It prioritises 1+1 ruleset achievability and production pass rates over estimates.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main Component ────────────────────────────────────────────────────

export function QuickQuestion() {
  const { user } = useAuthStore()
  const [searchParams] = useSearchParams()
  const [question, setQuestion] = useState('')
  const [asking, setAsking] = useState(false)
  const [currentAnswer, setCurrentAnswer] = useState<QuestionRecord | null>(null)
  const [pastQuestions, setPastQuestions] = useState<QuestionRecord[]>([])
  const [loading, setLoading] = useState(true)
  const sharedQParam = searchParams.get('question')
  const [sharedQuestionId] = useState<number | null>(sharedQParam ? Number(sharedQParam) : null)

  useEffect(() => {
    const load = async () => {
      const listRes = await matchRateApi.listQuestions()
      const list = unwrapList(listRes) as QuestionRecord[]

      // If a shared question ID is in the URL, ensure it's in the list
      if (sharedQuestionId) {
        const alreadyInList = list.some(q => q.id === sharedQuestionId)
        if (!alreadyInList) {
          try {
            const res = await matchRateApi.loadQuestion(sharedQuestionId)
            const data = unwrap(res) as QuestionRecord | null
            if (data) {
              list.unshift(data)
            } else {
              showToast('Shared question not found', 'error')
            }
          } catch {
            showToast('Failed to load shared question', 'error')
          }
        }
      }

      setPastQuestions(list)
      setLoading(false)
    }
    load()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id: number) => {
    await matchRateApi.deleteQuestion(id)
    setPastQuestions(prev => prev.filter(q => q.id !== id))
    if (currentAnswer?.id === id) setCurrentAnswer(null)
    showToast('Question deleted', 'success')
  }

  const handleAsk = async () => {
    if (!question.trim()) return
    setAsking(true)
    setCurrentAnswer(null)

    const res = await matchRateApi.ask({
      question: question.trim(),
      created_by: user?.email || undefined,
      created_by_name: user?.name || undefined,
    })

    const data = unwrap(res) as QuestionRecord | null
    if (data) {
      setCurrentAnswer(data)
      setPastQuestions(prev => [data, ...prev])
    } else {
      showToast(res?.error || 'Failed to get answer', 'error')
    }
    setAsking(false)
  }

  return (
    <div className="space-y-6">
      {/* Input */}
      <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare size={16} className="text-violet-400" />
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Ask a Question</h3>
        </div>
        <p className="text-xs text-[var(--text-muted)] mb-3">
          Ask anything about FrankieOne's KYC verification capabilities. The AI will consult the coverage database, data sources, and team insights to answer.
        </p>
        <textarea
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !asking) { e.preventDefault(); handleAsk() } }}
          placeholder="e.g. Can we do a 2+2 check in China? What data will the customer need?"
          rows={3}
          disabled={asking}
          className="w-full px-3 py-2.5 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-sm text-[var(--text-primary)] resize-none focus:outline-none focus:ring-1 focus:ring-violet-500/50"
        />
        <div className="flex items-center justify-between mt-3">
          <div className="flex gap-2 flex-wrap">
            {EXAMPLE_QUESTIONS.map((ex, i) => (
              <button
                key={i}
                onClick={() => setQuestion(ex)}
                disabled={asking}
                className="text-[10px] px-2 py-1 rounded-full bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
              >
                Example {i + 1}
              </button>
            ))}
          </div>
          <button
            onClick={handleAsk}
            disabled={asking || !question.trim()}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-violet-600 text-white hover:bg-violet-500 transition-colors disabled:opacity-50"
          >
            {asking ? (
              <><Loader2 size={14} className="animate-spin" /> Thinking...</>
            ) : (
              <><Sparkles size={14} /> Ask</>
            )}
          </button>
        </div>
      </div>

      {/* Current answer */}
      {currentAnswer?.answer && (
        <CurrentAnswerView answer={currentAnswer} />
      )}

      {/* Past questions */}
      <div>
        <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Past Questions</h3>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 size={16} className="animate-spin text-[var(--text-muted)]" /></div>
        ) : pastQuestions.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] text-center py-4">No questions asked yet.</p>
        ) : (
          <div className="space-y-2">
            {pastQuestions.map(q => <QuestionCard key={q.id} q={q} onDelete={handleDelete} defaultExpanded={q.id === sharedQuestionId} />)}
          </div>
        )}
      </div>

      {/* Data Sources & Methodology */}
      <MethodologyPanel />
    </div>
  )
}
